import { Note } from "@/models/types";
import { useState, useRef, useEffect } from "react";
import {
  StyleSheet,
  View,
  Text,
  Pressable,
  Animated,
  PanResponder,
  ActivityIndicator,
} from "react-native";
import { ms } from "@/ui/moderateScale";
import { Colors } from "@/constants";
import { useTranslation } from "react-i18next";
import Icon from "@/ui/Icon";
import { useUISlice } from "@/models/useUISlice";

export default function AIContentFlashcard({
  flashcards,
}: {
  flashcards: Note["listAIContent"][number]["flashcards"];
}) {
  const { t } = useTranslation();
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const flipAnim = useRef(new Animated.Value(0)).current;
  const swipeAnim = useRef(new Animated.Value(0)).current;
  const height = useUISlice((state) => state.device.height);
  const width = useUISlice((state) => state.device.width);

  // Reset animation values when card changes
  useEffect(() => {
    flipAnim.setValue(0);
    swipeAnim.setValue(0);
  }, [currentCardIndex]);

  const currentCard = flashcards[currentCardIndex];
  const totalCards = flashcards.length;
  const isLastCard = currentCardIndex === totalCards - 1;
  const isFirstCard = currentCardIndex === 0;

  // Flip animation
  const handleFlip = () => {
    setIsFlipped(!isFlipped);
    Animated.timing(flipAnim, {
      toValue: isFlipped ? 0 : 1,
      duration: 300,
      useNativeDriver: true,
    }).start();
  };

  // Card navigation with animation
  const handleNext = () => {
    if (!isLastCard) {
      Animated.timing(swipeAnim, {
        toValue: -1,
        duration: 200,
        useNativeDriver: true,
      }).start(() => {
        setCurrentCardIndex(currentCardIndex + 1);
        setIsFlipped(false);
        swipeAnim.setValue(0);
      });
    }
  };

  const handlePrevious = () => {
    if (!isFirstCard) {
      Animated.timing(swipeAnim, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start(() => {
        setCurrentCardIndex(currentCardIndex - 1);
        setIsFlipped(false);
        swipeAnim.setValue(0);
      });
    }
  };

  // Swipe gesture support
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderRelease: (_, gestureState) => {
        // Detect horizontal swipe
        if (Math.abs(gestureState.dx) > 50) {
          if (gestureState.dx < 0 && !isLastCard) {
            // Swipe left -> next
            handleNext();
          } else if (gestureState.dx > 0 && !isFirstCard) {
            // Swipe right -> previous
            handlePrevious();
          }
        }
      },
    })
  ).current;

  // Transform styles for the front and back of the card
  const frontInterpolate = flipAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "180deg"],
  });

  const backInterpolate = flipAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["180deg", "360deg"],
  });

  const frontOpacity = flipAnim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [1, 0, 0],
  });

  const backOpacity = flipAnim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0, 0, 1],
  });

  if (!flashcards || flashcards.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <ActivityIndicator color={Colors.text} />
      </View>
    );
  }

  return (
    <View style={[styles.container, { height: Math.min(width - ms(32), 400) }]}>
      <View style={styles.counterContainer}>
        <Text style={styles.counter}>
          {currentCardIndex + 1} / {totalCards}
        </Text>
        <Text style={styles.tapHint}>
          ({t("aiContentFlashcard.tapToFlip")})
        </Text>
      </View>

      <View style={styles.cardContainer}>
        <Pressable
          style={[
            styles.navButton,
            styles.leftNav,
            isFirstCard && styles.navButtonDisabled,
          ]}
          onPress={handlePrevious}
          disabled={isFirstCard}
        >
          <Icon icon="caretRight" style={styles.prevIcon} />
        </Pressable>

        <View {...panResponder.panHandlers} style={styles.cardWrapper}>
          <Animated.View
            style={[
              styles.card,
              styles.cardFront,
              {
                transform: [{ rotateY: frontInterpolate }],
                opacity: frontOpacity,
                zIndex: isFlipped ? 0 : 1,
              },
            ]}
          >
            <Pressable style={styles.cardContent} onPress={handleFlip}>
              <Text style={styles.cardText}>{currentCard.question}</Text>
            </Pressable>
          </Animated.View>

          <Animated.View
            style={[
              styles.card,
              styles.cardBack,
              {
                transform: [{ rotateY: backInterpolate }],
                opacity: backOpacity,
                zIndex: isFlipped ? 1 : 0,
              },
            ]}
          >
            <Pressable style={styles.cardContent} onPress={handleFlip}>
              <Text style={styles.cardText}>{currentCard.correctAnswer}</Text>
            </Pressable>
          </Animated.View>
        </View>

        <Pressable
          style={[
            styles.navButton,
            styles.rightNav,
            isLastCard && styles.navButtonDisabled,
          ]}
          onPress={handleNext}
          disabled={isLastCard}
        >
          <Icon icon="caretRight" style={styles.nextIcon} />
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderRadius: ms(12),
    padding: ms(16),
    overflow: "hidden",
    backgroundColor: Colors.white,
    // shadowColor: "#000",
    // shadowOffset: { width: 0, height: 2 },
    // shadowOpacity: 0.1,
    // shadowRadius: 8,
    borderWidth: 1,
    borderColor: Colors.gray4,
    elevation: 3,
    minHeight: ms(400),
    position: "relative",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: ms(16),
    borderRadius: ms(16),
    minHeight: ms(300),
  },
  emptyText: {
    fontSize: ms(16),
    color: Colors.subtitle,
    textAlign: "center",
  },
  counterContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: ms(16),
  },
  counter: {
    fontSize: ms(12),
    color: Colors.subtitle,
    opacity: 0.7,
  },
  cardContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    minHeight: ms(200),
    position: "relative",
  },
  cardWrapper: {
    flex: 1,
    height: "90%",
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    position: "absolute",
    width: "100%",
    height: "100%",
    backfaceVisibility: "hidden",
    justifyContent: "center",
    alignItems: "center",
    padding: ms(24),
    borderRadius: ms(16),
    backgroundColor: Colors.white,
  },
  cardFront: {},
  cardBack: {
    backgroundColor: Colors.white,
  },
  cardText: {
    fontSize: ms(18),
    fontWeight: "500",
    color: Colors.text,
    width: "85%",
    textAlign: "center",
    lineHeight: ms(32),
  },
  tapHint: {
    position: "absolute",
    top: ms(24),
    fontSize: ms(12),
    color: Colors.subtitle,
    opacity: 0.7,
  },
  navButton: {
    padding: ms(8),
    borderRadius: ms(20),
    backgroundColor: "rgba(240, 240, 247, 0.8)",
    zIndex: 10,
  },
  leftNav: {
    position: "absolute",
    left: 0,
    top: "50%",
    transform: [{ translateY: -ms(15) }],
  },
  rightNav: {
    position: "absolute",
    right: 0,
    top: "50%",
    transform: [{ translateY: -ms(15) }],
  },
  navButtonDisabled: {
    opacity: 0.4,
  },
  prevIcon: {
    width: ms(20),
    height: ms(20),
    transform: [{ rotate: "180deg" }],
  },
  nextIcon: {
    width: ms(20),
    height: ms(20),
  },
  cardContent: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
});
